import { ArrowRight, Eye, FileClock, SlidersHorizontal, X } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Avatar,
  AvatarFallback,
  Badge,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/shadcn/index.js';
import {
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Skeleton,
} from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';

interface AuditDto {
  id: string;
  actorUserId: string | null;
  actorName: string | null;
  action: string;
  operation: string | null;
  entityType: string;
  entityId: string | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  diff?: unknown;
  beforeState?: Record<string, unknown> | null;
  afterState?: Record<string, unknown> | null;
}

interface Filters {
  operation: string;
  entityType: string;
  actor: string;
  action: string;
  since: string;
  until: string;
}
const EMPTY: Filters = { operation: '', entityType: '', actor: '', action: '', since: '', until: '' };

const OPERATIONS = ['CREATE', 'UPDATE', 'DELETE'] as const;

function opTone(op: string | null): 'success' | 'warning' | 'danger' | 'secondary' {
  switch (op) {
    case 'CREATE': return 'success';
    case 'UPDATE': return 'warning';
    case 'DELETE': return 'danger';
    default: return 'secondary';
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '•';
}

function toQuery(f: Filters): string {
  const p = new URLSearchParams();
  p.set('limit', '200');
  if (f.operation) p.set('operation', f.operation);
  if (f.entityType) p.set('entityType', f.entityType);
  if (f.actor) p.set('actor', f.actor);
  if (f.action) p.set('action', f.action);
  if (f.since) p.set('since', f.since);
  if (f.until) p.set('until', `${f.until}T23:59:59`);
  return `?${p.toString()}`;
}

export function AuditLogPage(): JSX.Element {
  const { t, i18n } = useTranslation('audit');
  const [items, setItems] = useState<AuditDto[] | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    let live = true;
    setItems(null);
    void api()
      .get<{ items: AuditDto[] }>(`/audit-logs${toQuery(applied)}`)
      .then((r) => { if (live) setItems(r.data.items); });
    return () => { live = false; };
  }, [applied]);

  const expanded = useMemo(
    () => (expandedId ? items?.find((r) => r.id === expandedId) ?? null : null),
    [expandedId, items],
  );

  // Active-filter chips (each removable).
  const chips = useMemo(() => {
    const out: Array<{ key: keyof Filters; label: string }> = [];
    if (applied.operation) out.push({ key: 'operation', label: `${t('columns.operation')}: ${applied.operation}` });
    if (applied.entityType) out.push({ key: 'entityType', label: `${t('filters.entity', { defaultValue: 'Entity' })}: ${applied.entityType}` });
    if (applied.actor) out.push({ key: 'actor', label: `${t('filters.actor')}: ${applied.actor}` });
    if (applied.action) out.push({ key: 'action', label: `${t('filters.action')}: ${applied.action}` });
    if (applied.since) out.push({ key: 'since', label: `${t('filters.dateFrom', { defaultValue: 'From' })}: ${applied.since}` });
    if (applied.until) out.push({ key: 'until', label: `${t('filters.dateTo', { defaultValue: 'To' })}: ${applied.until}` });
    return out;
  }, [applied, t]);

  const clearChip = (key: keyof Filters): void => { setExpandedId(null); setApplied((f) => ({ ...f, [key]: '' })); };

  const columns: DataTableColumn<AuditDto>[] = [
    {
      id: 'createdAt',
      header: t('columns.createdAt'),
      sortValue: (r) => r.createdAt,
      cellClassName: 'whitespace-nowrap',
      cell: (r) => {
        const d = new Date(r.createdAt);
        return (
          <div className="leading-tight">
            <div className="font-medium text-fg">{formatDateTime(r.createdAt, { locale: i18n.language })}</div>
            <div className="text-[10px] tabular-nums text-fg-subtle">{d.toISOString().slice(11, 19)} UTC</div>
          </div>
        );
      },
    },
    {
      id: 'actor',
      header: t('columns.actor', { defaultValue: 'Actor' }),
      sortValue: (r) => r.actorName ?? '',
      searchValue: (r) => `${r.actorName ?? ''} ${r.actorUserId ?? ''}`,
      cell: (r) => {
        const name = r.actorName ?? t('system', { defaultValue: 'System' });
        return (
          <div className="flex items-center gap-2.5">
            <Avatar className="h-7 w-7 shrink-0 border border-border">
              <AvatarFallback className={`text-[10px] font-semibold ${r.actorName ? 'bg-brand/10 text-brand' : 'bg-bg-subtle text-fg-subtle'}`}>
                {r.actorName ? initials(r.actorName) : '⚙'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="truncate font-medium text-fg">{name}</div>
              {r.actorUserId && <div className="truncate font-mono text-[10px] text-fg-subtle">{r.actorUserId.slice(0, 8)}</div>}
            </div>
          </div>
        );
      },
    },
    {
      id: 'operation',
      header: t('columns.operation', { defaultValue: 'Action' }),
      sortValue: (r) => r.operation ?? r.action,
      searchValue: (r) => `${r.operation ?? ''} ${r.action}`,
      cell: (r) => (
        <div className="flex flex-col items-start gap-1">
          {r.operation && <Badge variant={opTone(r.operation)}>{r.operation}</Badge>}
          <span className="font-mono text-[10px] text-fg-muted">{r.action}</span>
        </div>
      ),
    },
    {
      id: 'entityType',
      header: t('columns.entityType'),
      sortValue: (r) => r.entityType,
      searchValue: (r) => r.entityType,
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate">{r.entityType}</div>
          {r.entityId && <div className="truncate font-mono text-[10px] text-fg-subtle">{r.entityId.slice(0, 8)}</div>}
        </div>
      ),
    },
    {
      id: 'ip',
      header: t('columns.ip'),
      sortValue: (r) => r.ip ?? '',
      searchValue: (r) => r.ip ?? '',
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (r) => r.ip ?? '—',
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      headClassName: 'w-16',
      cell: (r) => {
        const hasDetail = !!(r.beforeState || r.afterState || (r.diff && r.diff !== null));
        if (!hasDetail) return null;
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpandedId(r.id)}
            aria-label={t('diff.show')}
            title={t('diff.show')}
          >
            <Eye className="h-4 w-4" aria-hidden />
          </Button>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-fg-muted">{t('subtitle')}</p>
        </div>
        <Button variant="secondary" onClick={() => setFilterOpen(true)} className="gap-2">
          <SlidersHorizontal className="h-4 w-4" aria-hidden />
          {t('filters.button', { defaultValue: 'Filter' })}
          {chips.length > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-[11px] font-bold text-brand-contrast">
              {chips.length}
            </span>
          )}
        </Button>
      </header>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => clearChip(c.key)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-bg-subtle px-2.5 py-1 text-xs text-fg transition hover:border-danger/40 hover:text-danger"
            >
              {c.label}
              <X className="h-3 w-3" aria-hidden />
            </button>
          ))}
          <button type="button" onClick={() => { setExpandedId(null); setApplied(EMPTY); }} className="text-xs font-medium text-fg-muted underline-offset-4 hover:text-fg hover:underline">
            {t('filters.clearAll', { defaultValue: 'Clear all' })}
          </button>
        </div>
      )}

      {!items ? (
        <Card>
          <Skeleton className="h-6 w-32" />
          <Skeleton className="mt-4 h-32 w-full" />
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<FileClock className="h-8 w-8" aria-hidden />}
          title={chips.length > 0 ? t('noMatches', { defaultValue: 'No events match these filters' }) : t('empty')}
          description={chips.length > 0 ? t('noMatchesDescription', { defaultValue: 'Try widening or clearing the filters.' }) : t('emptyDescription')}
          action={chips.length > 0 ? <Button variant="secondary" onClick={() => setApplied(EMPTY)}>{t('filters.clearAll', { defaultValue: 'Clear all' })}</Button> : undefined}
        />
      ) : (
        <>
          <Card className="overflow-hidden p-0">
            <DataTable
              columns={columns}
              data={items}
              rowKey={(r) => r.id}
              searchPlaceholder={t('searchPlaceholder')}
              rowClassName={(r) => (r.id === expandedId ? 'bg-bg-muted/40' : undefined)}
            />
          </Card>
        </>
      )}

      <FilterDialog
        open={filterOpen}
        value={applied}
        onClose={() => setFilterOpen(false)}
        onApply={(next) => { setExpandedId(null); setApplied(next); setFilterOpen(false); }}
        t={t}
      />

      {/* Change-details modal: the row's complete before/after diff. */}
      <Dialog open={!!expanded} onOpenChange={(o) => { if (!o) setExpandedId(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileClock className="h-4 w-4 text-fg-muted" aria-hidden />
              {t('diff.title', { defaultValue: 'Change details' })}
            </DialogTitle>
          </DialogHeader>
          {expanded && <ChangeViewer row={expanded} t={t} locale={i18n.language} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

type TFn = (k: string, o?: Record<string, unknown>) => string;

function FilterDialog({
  open, value, onClose, onApply, t,
}: {
  open: boolean;
  value: Filters;
  onClose: () => void;
  onApply: (f: Filters) => void;
  t: TFn;
}): JSX.Element {
  const [draft, setDraft] = useState<Filters>(value);
  // Re-sync the draft to the applied filters whenever the dialog is opened.
  useEffect(() => { if (open) setDraft(value); }, [open, value]);

  const set = (patch: Partial<Filters>): void => setDraft((d) => ({ ...d, ...patch }));
  const inputCls = 'block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-brand" aria-hidden />
            {t('filters.title', { defaultValue: 'Filter audit log' })}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="f-op" label={t('columns.operation', { defaultValue: 'Action' })}>
            <select id="f-op" className={inputCls} value={draft.operation} onChange={(e) => set({ operation: e.target.value })}>
              <option value="">{t('filters.allOperations', { defaultValue: 'All actions' })}</option>
              {OPERATIONS.map((op) => <option key={op} value={op}>{op}</option>)}
            </select>
          </FormField>
          <FormField id="f-actor" label={t('filters.actor')}>
            <Input id="f-actor" value={draft.actor} onChange={(e) => set({ actor: e.target.value })} placeholder={t('filters.actorPlaceholder', { defaultValue: 'Name contains…' })} />
          </FormField>
          <FormField id="f-entity" label={t('filters.entity', { defaultValue: 'Entity' })}>
            <Input id="f-entity" value={draft.entityType} onChange={(e) => set({ entityType: e.target.value })} placeholder={t('filters.entityPlaceholder', { defaultValue: 'e.g. invoices, parties' })} />
          </FormField>
          <FormField id="f-action" label={t('filters.action')}>
            <Input id="f-action" value={draft.action} onChange={(e) => set({ action: e.target.value })} placeholder={t('filters.actionPlaceholder', { defaultValue: 'e.g. invoices.post' })} />
          </FormField>
          <FormField id="f-since" label={t('filters.dateFrom', { defaultValue: 'From' })}>
            <Input id="f-since" type="date" value={draft.since} onChange={(e) => set({ since: e.target.value })} />
          </FormField>
          <FormField id="f-until" label={t('filters.dateTo', { defaultValue: 'To' })}>
            <Input id="f-until" type="date" value={draft.until} onChange={(e) => set({ until: e.target.value })} />
          </FormField>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={() => setDraft(EMPTY)}>{t('filters.reset', { defaultValue: 'Reset' })}</Button>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>{t('filters.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={() => onApply(draft)}>{t('filters.apply', { defaultValue: 'Apply filters' })}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangeViewer({ row, t, locale }: { row: AuditDto; t: TFn; locale: string }): JSX.Element {
  const [showFull, setShowFull] = useState(false);
  const before = (row.beforeState ?? null) as Record<string, unknown> | null;
  const after = (row.afterState ?? null) as Record<string, unknown> | null;

  const changed = useMemo(() => {
    const keys = Array.from(new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]));
    return keys
      .filter((k) => JSON.stringify((before ?? {})[k]) !== JSON.stringify((after ?? {})[k]))
      .sort();
  }, [before, after]);

  const hasStructured = before !== null || after !== null;

  return (
    <Card className="bg-bg-muted/30">
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        {row.operation && <Badge variant={opTone(row.operation)}>{row.operation}</Badge>}
        <span className="font-medium text-fg">{row.entityType}</span>
        <span className="text-fg-muted">·</span>
        <span className="text-fg-muted">{row.actorName ?? t('system', { defaultValue: 'System' })}</span>
        <span className="text-fg-muted">·</span>
        <span className="text-fg-muted">{formatDateTime(row.createdAt, { locale })}</span>
      </div>

      {!hasStructured ? (
        <pre className="max-h-72 overflow-auto rounded-md border border-border bg-bg p-3 text-xs">
          {JSON.stringify(row.diff ?? {}, null, 2)}
        </pre>
      ) : (
        <div className="flex flex-col gap-4">
          {changed.length > 0 ? (
            <dl className="grid grid-cols-[minmax(8rem,auto)_1fr] gap-x-4 gap-y-1.5 rounded-md border border-border bg-bg p-3 text-xs">
              {changed.map((k) => (
                <Fragment key={k}>
                  <dt className="font-mono text-fg-muted">{k}</dt>
                  <dd className="flex flex-wrap items-baseline gap-2">
                    <span className="rounded bg-danger/10 px-1.5 py-0.5 font-mono text-danger line-through decoration-1">
                      {fmt((before ?? {})[k])}
                    </span>
                    <ArrowRight className="h-3 w-3 flex-shrink-0 text-fg-muted rtl:rotate-180" aria-hidden />
                    <span className="rounded bg-success/10 px-1.5 py-0.5 font-mono text-success">
                      {fmt((after ?? {})[k])}
                    </span>
                  </dd>
                </Fragment>
              ))}
            </dl>
          ) : (
            <p className="text-sm text-fg-muted">{t('diff.noChanges')}</p>
          )}

          <div>
            <Button variant="ghost" size="sm" onClick={() => setShowFull((v) => !v)}>
              {showFull ? t('diff.hideFull', { defaultValue: 'Hide complete states' }) : t('diff.showFull', { defaultValue: 'Show complete before & after' })}
            </Button>
            {showFull && (
              <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                <StatePane title={t('diff.before')} state={before} empty={t('diff.none', { defaultValue: '— (did not exist)' })} />
                <StatePane title={t('diff.after')} state={after} empty={t('diff.none', { defaultValue: '— (deleted)' })} />
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function StatePane({ title, state, empty }: { title: string; state: Record<string, unknown> | null; empty: string }): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-bg">
      <div className="border-b border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-fg-subtle">{title}</div>
      {state === null ? (
        <div className="p-3 text-xs text-fg-muted">{empty}</div>
      ) : (
        <pre className="max-h-72 overflow-auto p-3 text-xs leading-relaxed">{JSON.stringify(state, null, 2)}</pre>
      )}
    </div>
  );
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'string') return v.length === 0 ? '""' : v;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
